import { useCallback, useState } from "react";
import { createTextRegistryAdminApi } from "../../../services/textRegistryAdminApi";
import type { TextKeyHistoryEntry, TextRegistryScope } from "./types";

type HistoryState = {
	entries: TextKeyHistoryEntry[];
	loading: boolean;
	error: string | null;
};

export function useTextHistory(scope: TextRegistryScope) {
	const [historyMap, setHistoryMap] = useState<Record<string, HistoryState>>({});

	const loadHistory = useCallback(
		async (key: string) => {
			setHistoryMap((prev) => ({
				...prev,
				[key]: { entries: prev[key]?.entries || [], loading: true, error: null },
			}));
			try {
				const api = createTextRegistryAdminApi(scope);
				const entries = await api.getKeyHistory(key);
				setHistoryMap((prev) => ({ ...prev, [key]: { entries, loading: false, error: null } }));
			} catch {
				setHistoryMap((prev) => ({
					...prev,
					[key]: { entries: [], loading: false, error: "Failed to load history." },
				}));
			}
		},
		[scope]
	);

	const getHistory = useCallback(
		(key: string): HistoryState => {
			return historyMap[key] || { entries: [], loading: false, error: null };
		},
		[historyMap]
	);

	const clearHistory = useCallback((key: string) => {
		setHistoryMap((prev) => {
			const next = { ...prev };
			delete next[key];
			return next;
		});
	}, []);

	return { loadHistory, getHistory, clearHistory };
}
